import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useChatSocket } from "@/hooks/useChatSocket";

/**
 * CallContext — 1:1 WebRTC voice/video calls.
 *
 * State machine:
 *   idle → outgoing_ringing → connected → ended (→ idle)
 *          incoming_ringing → connected → ended (→ idle)
 *
 * Signaling is over the same /api/ws WebSocket that the rest of the app
 * uses (its own connection here; we don't share ChatApp's ws so we can
 * receive incoming calls from any page).
 */

const CallContext = createContext(null);

const genCallId = () =>
  "call_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);

export function CallProvider({ children }) {
  const { user } = useAuth() || {};
  const [state, setState] = useState("idle"); // idle | outgoing | incoming | connected | ended
  const [callId, setCallId] = useState(null);
  const [callType, setCallType] = useState(null); // audio | video
  const [peer, setPeer] = useState(null); // {id, name, picture}
  const [error, setError] = useState("");
  const [muted, setMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);
  const [connectedAt, setConnectedAt] = useState(null);
  const [lastEndedInfo, setLastEndedInfo] = useState(null); // for post-call toast

  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const iceServersRef = useRef([{ urls: "stun:stun.l.google.com:19302" }]);
  const wsSendRef = useRef(() => {});
  const pendingRemoteIceRef = useRef([]);
  const outgoingRingTimerRef = useRef(null);
  const ringtoneRef = useRef(null);
  const outgoingBeepRef = useRef(null);

  // Fetch ICE servers once (or every time user logs in)
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/calls/ice-servers");
        if (Array.isArray(data?.iceServers) && data.iceServers.length) {
          iceServersRef.current = data.iceServers;
        }
      } catch { /* keep default STUN */ }
    })();
  }, [user]);

  const stopRingtone = useCallback(() => {
    try { ringtoneRef.current?.stop(); } catch {}
    ringtoneRef.current = null;
    try { outgoingBeepRef.current?.stop(); } catch {}
    outgoingBeepRef.current = null;
  }, []);

  const playRingtone = useCallback(() => {
    // Loopy sine chirp using WebAudio; free & no assets needed.
    try {
      const Ctor = window.AudioContext || window.webkitAudioContext;
      if (!Ctor) return;
      const ctx = new Ctor();
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.001, ctx.currentTime);
      gain.connect(ctx.destination);
      let stopped = false;
      const beep = () => {
        if (stopped) return;
        const t = ctx.currentTime;
        const o = ctx.createOscillator();
        o.type = "sine";
        o.frequency.setValueAtTime(660, t);
        o.frequency.setValueAtTime(820, t + 0.35);
        o.connect(gain);
        gain.gain.linearRampToValueAtTime(0.18, t + 0.02);
        gain.gain.linearRampToValueAtTime(0.001, t + 0.7);
        o.start(t);
        o.stop(t + 0.75);
        setTimeout(beep, 1200);
      };
      beep();
      ringtoneRef.current = {
        stop: () => {
          stopped = true;
          try { gain.disconnect(); } catch {}
          try { ctx.close(); } catch {}
        },
      };
    } catch {}
  }, []);

  const playOutgoingBeep = useCallback(() => {
    try {
      const Ctor = window.AudioContext || window.webkitAudioContext;
      if (!Ctor) return;
      const ctx = new Ctor();
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.001, ctx.currentTime);
      gain.connect(ctx.destination);
      let stopped = false;
      const beep = () => {
        if (stopped) return;
        const t = ctx.currentTime;
        const o = ctx.createOscillator();
        o.type = "sine";
        o.frequency.setValueAtTime(480, t);
        o.connect(gain);
        gain.gain.linearRampToValueAtTime(0.12, t + 0.03);
        gain.gain.linearRampToValueAtTime(0.001, t + 0.4);
        o.start(t);
        o.stop(t + 0.42);
        setTimeout(beep, 1400);
      };
      beep();
      outgoingBeepRef.current = {
        stop: () => {
          stopped = true;
          try { gain.disconnect(); } catch {}
          try { ctx.close(); } catch {}
        },
      };
    } catch {}
  }, []);

  const cleanupCall = useCallback(() => {
    clearTimeout(disconnectGraceRef.current);
    disconnectGraceRef.current = null;
    try { pcRef.current?.close(); } catch {}
    pcRef.current = null;
    try { localStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    localStreamRef.current = null;
    try { remoteStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    remoteStreamRef.current = null;
    pendingRemoteIceRef.current = [];
    clearTimeout(outgoingRingTimerRef.current);
    outgoingRingTimerRef.current = null;
    stopRingtone();
    setMuted(false);
    setCameraOff(false);
  }, [stopRingtone]);

  const resetAll = useCallback(() => {
    cleanupCall();
    setCallId(null);
    setCallType(null);
    setPeer(null);
    setConnectedAt(null);
    setState("idle");
  }, [cleanupCall]);

  const disconnectGraceRef = useRef(null);

  const newPC = useCallback(() => {
    const pc = new RTCPeerConnection({ iceServers: iceServersRef.current });
    const remote = new MediaStream();
    remoteStreamRef.current = remote;
    pc.ontrack = (evt) => {
      evt.streams[0].getTracks().forEach((t) => remote.addTrack(t));
    };
    pc.onicecandidate = (evt) => {
      if (evt.candidate) {
        wsSendRef.current?.({ type: "call_ice", call_id: callIdRef.current, candidate: evt.candidate });
      }
    };
    pc.onconnectionstatechange = () => {
      // "disconnected" is often transient (Wi-Fi flap, NAT rebind). Give the
      // ICE agent up to 15s to recover before ending the call. "failed" is
      // terminal — end immediately.
      if (pc.connectionState === "failed" || pc.connectionState === "closed") {
        clearTimeout(disconnectGraceRef.current);
        endCallLocal("network");
      } else if (pc.connectionState === "disconnected") {
        clearTimeout(disconnectGraceRef.current);
        disconnectGraceRef.current = setTimeout(() => {
          // If we're still not back to "connected" after 15s, drop the call.
          if (pcRef.current === pc && pc.connectionState !== "connected") {
            endCallLocal("network");
          }
        }, 15000);
      } else if (pc.connectionState === "connected") {
        clearTimeout(disconnectGraceRef.current);
        disconnectGraceRef.current = null;
      }
    };
    pcRef.current = pc;
    return pc;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // We need refs for callback stability
  const callIdRef = useRef(null);
  useEffect(() => { callIdRef.current = callId; }, [callId]);
  const stateRef = useRef(state);
  useEffect(() => { stateRef.current = state; }, [state]);

  /**
   * Check the current browser-level permission for mic (and optionally cam)
   * WITHOUT triggering a prompt. Returns 'granted' | 'denied' | 'prompt' | 'unknown'.
   * navigator.permissions is not implemented everywhere (Safari partial), so we
   * gracefully degrade to 'unknown' and let getUserMedia handle it.
   */
  const queryMediaPermission = useCallback(async (needsVideo) => {
    try {
      if (!navigator.permissions?.query) return "unknown";
      const mic = await navigator.permissions.query({ name: "microphone" });
      let cam = { state: "granted" };
      if (needsVideo) {
        try { cam = await navigator.permissions.query({ name: "camera" }); } catch { cam = { state: "unknown" }; }
      }
      if (mic.state === "denied" || cam.state === "denied") return "denied";
      if (mic.state === "prompt" || cam.state === "prompt") return "prompt";
      if (mic.state === "granted" && (!needsVideo || cam.state === "granted")) return "granted";
      return "unknown";
    } catch { return "unknown"; }
  }, []);

  const startLocalStream = useCallback(async (type) => {
    const constraints = {
      audio: true,
      video: type === "video" ? { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: "user" } : false,
    };
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    localStreamRef.current = stream;
    return stream;
  }, []);

  const startCall = useCallback(async ({ toUser, type }) => {
    setError("");
    if (stateRef.current !== "idle") return;
    // Pre-check: if permission was previously denied by the user, don't even
    // flash the "Aranıyor…" overlay — surface an actionable toast instead.
    const perm = await queryMediaPermission(type === "video");
    if (perm === "denied") {
      setLastEndedInfo({
        status: "permission_denied",
        peer: toUser,
        needsVideo: type === "video",
      });
      return;
    }
    const id = genCallId();
    setCallId(id);
    callIdRef.current = id;
    setCallType(type);
    setPeer(toUser);
    setState("outgoing");
    try {
      const stream = await startLocalStream(type);
      const pc = newPC();
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      wsSendRef.current?.({
        type: "call_offer",
        call_id: id,
        to: toUser.id,
        call_type: type,
        sdp: offer,
      });
      playOutgoingBeep();
      // 45s auto-cancel if no answer
      outgoingRingTimerRef.current = setTimeout(() => {
        wsSendRef.current?.({ type: "call_cancel", call_id: id });
        setLastEndedInfo({ status: "no_answer", peer: toUser });
        resetAll();
      }, 45000);
    } catch (e) {
      const name = e?.name || "";
      const raw = e?.message || "";
      const denied = /NotAllowedError|Permission denied|denied/i.test(name + " " + raw);
      const notFound = /NotFoundError|OverconstrainedError|DevicesNotFoundError/i.test(name + " " + raw);
      wsSendRef.current?.({ type: "call_cancel", call_id: id });
      if (denied) {
        setLastEndedInfo({
          status: "permission_denied",
          peer: toUser,
          needsVideo: type === "video",
        });
      } else if (notFound) {
        setLastEndedInfo({
          status: "media_failed",
          peer: toUser,
          message: type === "video" ? "Kamera veya mikrofon bulunamadı" : "Mikrofon bulunamadı",
        });
      } else {
        setLastEndedInfo({
          status: "media_failed",
          peer: toUser,
          message: "Kamera/mikrofon açılamadı",
        });
      }
      resetAll();
    }
  }, [newPC, queryMediaPermission, startLocalStream, playOutgoingBeep, resetAll]);

  const acceptCall = useCallback(async (remoteOfferSdp) => {
    if (stateRef.current !== "incoming") return;
    // Pre-check permission — if denied, reject the incoming call politely
    // and surface the "how to re-enable" toast so the user isn't stuck.
    const perm = await queryMediaPermission(callType === "video");
    if (perm === "denied") {
      const savedPeer = peer;
      const needsV = callType === "video";
      if (callIdRef.current) wsSendRef.current?.({ type: "call_reject", call_id: callIdRef.current });
      setLastEndedInfo({ status: "permission_denied", peer: savedPeer, needsVideo: needsV });
      resetAll();
      return;
    }
    stopRingtone();
    try {
      const stream = await startLocalStream(callType);
      const pc = newPC();
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      await pc.setRemoteDescription(remoteOfferSdp);
      // Drain any early ICE candidates
      for (const c of pendingRemoteIceRef.current) {
        try { await pc.addIceCandidate(c); } catch {}
      }
      pendingRemoteIceRef.current = [];
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      wsSendRef.current?.({ type: "call_answer", call_id: callIdRef.current, sdp: answer });
      setConnectedAt(Date.now());
      setState("connected");
    } catch (e) {
      const name = e?.name || "";
      const raw = e?.message || "";
      const denied = /NotAllowedError|Permission denied|denied/i.test(name + " " + raw);
      const notFound = /NotFoundError|OverconstrainedError|DevicesNotFoundError/i.test(name + " " + raw);
      const savedPeer = peer;
      const needsV = callType === "video";
      if (callIdRef.current) wsSendRef.current?.({ type: "call_reject", call_id: callIdRef.current });
      if (denied) {
        setLastEndedInfo({ status: "permission_denied", peer: savedPeer, needsVideo: needsV });
      } else if (notFound) {
        setLastEndedInfo({ status: "media_failed", peer: savedPeer, message: needsV ? "Kamera veya mikrofon bulunamadı" : "Mikrofon bulunamadı" });
      } else {
        setLastEndedInfo({ status: "media_failed", peer: savedPeer, message: "Aramaya bağlanılamadı" });
      }
      resetAll();
    }
  }, [callType, newPC, peer, queryMediaPermission, startLocalStream, stopRingtone, resetAll]);

  const rejectCall = useCallback(() => {
    if (callIdRef.current) wsSendRef.current?.({ type: "call_reject", call_id: callIdRef.current });
    setLastEndedInfo({ status: "rejected", peer });
    resetAll();
  }, [peer, resetAll]);

  const endCallLocal = useCallback((reason = "hangup") => {
    if (callIdRef.current) {
      const t = stateRef.current === "outgoing" ? "call_cancel" : "call_hangup";
      wsSendRef.current?.({ type: t, call_id: callIdRef.current });
    }
    setLastEndedInfo({ status: reason, peer });
    resetAll();
  }, [peer, resetAll]);

  const toggleMute = useCallback(() => {
    const s = localStreamRef.current;
    if (!s) return;
    s.getAudioTracks().forEach((t) => { t.enabled = !t.enabled; });
    setMuted((m) => !m);
  }, []);

  const toggleCamera = useCallback(() => {
    const s = localStreamRef.current;
    if (!s) return;
    s.getVideoTracks().forEach((t) => { t.enabled = !t.enabled; });
    setCameraOff((c) => !c);
  }, []);

  // ---------------- WS message handler ----------------
  const onMessage = useCallback(async (msg) => {
    if (!msg?.type) return;
    const t = msg.type;
    if (t === "call_offer") {
      // If we're already busy, decline via signaling
      if (stateRef.current !== "idle") {
        wsSendRef.current?.({ type: "call_busy", call_id: msg.call_id });
        return;
      }
      // Stash the offer SDP so acceptCall can use it later
      pendingRemoteIceRef.current = [];
      window.__yappo_pending_offer = msg.sdp;
      setCallId(msg.call_id);
      callIdRef.current = msg.call_id;
      setCallType(msg.call_type || "audio");
      setPeer(msg.from);
      setState("incoming");
      playRingtone();
    } else if (t === "call_answer" && msg.call_id === callIdRef.current) {
      clearTimeout(outgoingRingTimerRef.current);
      stopRingtone();
      const pc = pcRef.current;
      if (!pc) return;
      try {
        await pc.setRemoteDescription(msg.sdp);
        for (const c of pendingRemoteIceRef.current) {
          try { await pc.addIceCandidate(c); } catch {}
        }
        pendingRemoteIceRef.current = [];
        setConnectedAt(Date.now());
        setState("connected");
      } catch (e) {
        setError("Bağlantı kurulamadı");
        endCallLocal("failed");
      }
    } else if (t === "call_ice" && msg.call_id === callIdRef.current) {
      const cand = msg.candidate;
      const pc = pcRef.current;
      if (pc && pc.remoteDescription) {
        try { await pc.addIceCandidate(cand); } catch {}
      } else {
        pendingRemoteIceRef.current.push(cand);
      }
    } else if (t === "call_reject" && msg.call_id === callIdRef.current) {
      setLastEndedInfo({ status: "rejected_by_peer", peer });
      resetAll();
    } else if (t === "call_cancel" && msg.call_id === callIdRef.current) {
      setLastEndedInfo({ status: "canceled_by_peer", peer });
      resetAll();
    } else if (t === "call_hangup" && msg.call_id === callIdRef.current) {
      setLastEndedInfo({ status: msg.reason === "peer_disconnected" ? "peer_left" : "ended", peer });
      resetAll();
    } else if (t === "call_busy" && msg.call_id === callIdRef.current) {
      setLastEndedInfo({ status: "busy", peer });
      resetAll();
    } else if (t === "call_error" && msg.call_id === callIdRef.current) {
      setError(msg.reason === "not_friends" ? "Yalnızca arkadaşlarını arayabilirsin" : "Arama başlatılamadı");
      resetAll();
    }
  }, [endCallLocal, peer, playRingtone, resetAll, stopRingtone]);

  const { send } = useChatSocket({ enabled: !!user, onMessage });
  useEffect(() => { wsSendRef.current = send; }, [send]);

  // Keep the browser tab / OS aware that a call is active so users can
  // safely switch to other apps without the call being aggressively
  // suspended. Two mechanisms:
  //   1) Update document.title with a red dot marker so the tab is
  //      distinguishable from other Yappo tabs.
  //   2) Request a Screen Wake Lock (best-effort — supported on Chrome/Edge,
  //      newer Safari) so the phone screen doesn't turn off during a video
  //      call, which on iOS/Android would tear down the WebRTC connection.
  const wakeLockRef = useRef(null);
  const savedTitleRef = useRef(null);
  useEffect(() => {
    const isActive = state === "connected" || state === "outgoing" || state === "incoming";
    if (isActive) {
      if (savedTitleRef.current == null) savedTitleRef.current = document.title;
      const prefix = state === "incoming" ? "📞 Gelen arama · " : state === "outgoing" ? "📞 Aranıyor · " : "🔴 Aramada · ";
      document.title = prefix + (peer?.name || "Yappo");
      // Best-effort wake lock — only meaningful when connected
      if (state === "connected" && !wakeLockRef.current) {
        (async () => {
          try {
            if (navigator.wakeLock && typeof navigator.wakeLock.request === "function") {
              wakeLockRef.current = await navigator.wakeLock.request("screen");
              wakeLockRef.current.addEventListener?.("release", () => { wakeLockRef.current = null; });
            }
          } catch { /* ignore — the call still works, screen just won't stay awake */ }
        })();
      }
    } else {
      if (savedTitleRef.current != null) {
        document.title = savedTitleRef.current;
        savedTitleRef.current = null;
      }
      const wl = wakeLockRef.current;
      if (wl) {
        try { wl.release?.(); } catch {}
        wakeLockRef.current = null;
      }
    }
  }, [state, peer]);

  // Re-request the wake lock when the tab becomes visible again — Chrome
  // silently releases it when the page is backgrounded. This is the whole
  // reason "arka planda arama" works reliably.
  useEffect(() => {
    const onVisible = async () => {
      if (document.visibilityState !== "visible") return;
      if (stateRef.current !== "connected") return;
      if (wakeLockRef.current) return;
      try {
        if (navigator.wakeLock && typeof navigator.wakeLock.request === "function") {
          wakeLockRef.current = await navigator.wakeLock.request("screen");
        }
      } catch {}
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  // Auto-clear ended toast after 5s — EXCEPT for permission_denied which is
  // dismissed manually so the user can read the instructions.
  useEffect(() => {
    if (!lastEndedInfo) return;
    if (lastEndedInfo.status === "permission_denied") return;
    const id = setTimeout(() => setLastEndedInfo(null), 5000);
    return () => clearTimeout(id);
  }, [lastEndedInfo]);

  const pendingOffer = window.__yappo_pending_offer;

  const value = useMemo(
    () => ({
      state, callId, callType, peer, error, muted, cameraOff, connectedAt, lastEndedInfo,
      localStream: localStreamRef,
      remoteStream: remoteStreamRef,
      startCall,
      acceptCall: () => acceptCall(pendingOffer),
      rejectCall,
      endCall: endCallLocal,
      toggleMute,
      toggleCamera,
      clearEndedToast: () => setLastEndedInfo(null),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, callId, callType, peer, error, muted, cameraOff, connectedAt, lastEndedInfo,
     startCall, acceptCall, rejectCall, endCallLocal, toggleMute, toggleCamera]
  );

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
}

export function useCall() {
  const ctx = useContext(CallContext);
  if (!ctx) throw new Error("useCall must be used inside CallProvider");
  return ctx;
}
